/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */
import {React, useEffect, useState} from 'react';
import Modal from '@material-ui/core/Modal';
import Typography from '@material-ui/core/Typography';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import FormGroup from "@material-ui/core/FormGroup";
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Button from "@material-ui/core/Button";
import {InputLabel} from "@material-ui/core";
import QuestionEditor from './editors/QuestionEditor.jsx';
import SourceIdEditor from './editors/SourceIdEditor.jsx';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import {CloseButton, SaveButton, QuestionIndexSelection} from "../common/CommonButtons";
import AddIcon from "@material-ui/icons/Add";
import MenuItem from "@material-ui/core/MenuItem";
import SQSService from "../../js/service/SQSService";
import {useAlert} from "react-alert";
import '../../css/views/CreateResponseFormView.css'
import '../../css/views/Views.css'
import '../../css/Common.css'
import '../../css/views/editors/QuestionEditor.css'
import {generateUniqueID} from "web-vitals/dist/modules/lib/generateUniqueID";
import HttpService from '../../js/service/HttpService.js';
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import DynamoDBService from "../../js/service/DynamoDBService";

/**
 * Component representing the create response form view.
 *
 * @prop open             true if component is showing
 * @prop setOpen          function to close component from view
 * @prop publishTo        where to publish response on save
 * @prop updateTable      function to update table on save
 * @prop surveyDefinition survey definition to use
 */
export default function ResponseFormView(props) {

    const httpService = new HttpService(props.oktaAuth);
    const sqsService = new SQSService(httpService);
    const dbService = new DynamoDBService(httpService);

    // Label for title (Edit or Create)
    let titleLabel = "Edit";

    // Label for warning dialog (Update or Add)
    let warningLabel = "Update";

    /**
     * Gets current response of this form
     *
     * @return current response, or default if it doesn't exist.
     */
    const getCurrentResponse = () => {
        let response = props.response;

        // If response doesn't exist, set template
        if (!response) {

            titleLabel = "Create";
            warningLabel = "Add";

            response = {
                sourceId : "",
                sortKey : "",
                surveyId : "",
                questions : {},
            };
        }

        if (!response.questions) {
            response.questions = {};
        }

        return response;
    }
    const originalResponse = getCurrentResponse();

    const alert = useAlert();

    /**
     * Publishes to SQS given a response
     *
     * @param response response to publish
     */
    const publishSQS = () => {
        let response = getUpdatedResponse()

        let sqsMessage = {
            surveyResponse : response
        }

        dbService.put_into_avx(sqsMessage.surveyResponse, alert, props.updateTable);
        sqsService.sendMessage(sqsMessage, alert, props.updateTable);

        handleClose();
        handleWarningDialogClose();
    }

    /**
     * Returns a new response with data from form
     *
     * @return updated response
     */
    const getUpdatedResponse = () => {
        return {
            sourceId: sourceId,
            surveyId: props.surveyDefinition.id,
            questions: Object.values(questions),
            shareWithProvider: requirePatientConsent
        };
    }

    /**
     * Handles closing the component from view.
     */
    const handleClose = () => {
        setSourceId('');
        setSelectedDayOffset('');
        setDayOffsetSet(false)
        setOrigin('device');
        setDeviceSerial('');
        setPatientId('');
        setRequirePatientConsent(false)
        setQuestionEditors(null);
        setQuestions(mapQuestions());
        props.setOpen(false);
        setDayOffsetSet(false);
    }

    /**
     * Handles closing the warning dialog
     */
    const handleWarningDialogOpen = () => {
        setWarningDialogOpen(true);
    }

    /**
     * Handles opening the warning dialog
     */
    const handleWarningDialogClose = () => {
        setWarningDialogOpen(false);
    }

    /**
     * Handles opening the question selection menu
     *
     * @param event menu event
     */
    const handleQuestionMenuOpen = (event) => {
        setQuestionAnchorEl(event.currentTarget);
    }

    /**
     * Handles closing the question selection menu
     */
    const handleQuestionMenuClose = () => {
        setQuestionAnchorEl(null);
    };

    /**
     * Handles changing the day offset value
     */
    const handleDayOffsetChange = (event) => {
        setSelectedDayOffset(event.target.value);
        let questionTemp = [];
        let selectedOffset = parseInt(event.target.value);
        for (let question in props.surveyDefinition.questions) {
            if (props.surveyDefinition.questions[question].dayOffset.includes(selectedOffset)){
                let tempQuestion = {
                    questionId: props.surveyDefinition.questions[question].id,
                    origin: origin,
                    deviceSerial: deviceSerial
                }
                if (patientId) {
                    tempQuestion['patientId'] = patientId;
                }
                questionTemp.push(tempQuestion);
            }
        }
        setQuestions(questionTemp);

        let questionEditors = [];
        for (let key in questionTemp) {
            questionEditors.push(
                <QuestionEditor
                    key={key}
                    questionKey={key}
                    question={questionTemp[key]}
                    deleteQuestion={deleteQuestion}
                    updateQuestion={updateQuestion}
                    questionDefinitions={props.surveyDefinition.questions}
                    dayOffset={event.target.value}
                    origin={origin}
                    deviceSerial={deviceSerial}
                    patientId={patientId}
                    langJson={props.langJson}
                />
            )
        }

        setQuestionEditors(questionEditors)
    };

    /**
     * Handles opening JSON preview
     */
    const handleJsonPreviewOpen = () => {
        setJsonPreviewOpen(true);
        setJsonPreview(getUpdatedResponse());
    }

    /**
     * Handles saving the response by setting the json to submit
     */
    const handleSave = () => {
        handleWarningDialogOpen();
    };

    const handleOriginChange = (event) => {
        setOrigin(event.target.value);
    }

    const handleDeviceSerialChange = (event) => {
        setDeviceSerial(event.target.value);
    }

    const handlePatientIdChange = (event) => {
        setPatientId(event.target.value);
    }

    /**
     * Updates the source id of response
     *
     * @param sourceId
     */
    const updateSourceId = (sourceId) => {
        setSourceId(sourceId);
    }

    const updateRequirePatientConsent = () => {
        setRequirePatientConsent(!requirePatientConsent)
    }

    /**
     * Updates question in list given the index
     *
     * @param question new data
     * @param key    key of question
     */
    const updateQuestion = (question, key) => {
        let newQuestions = Object.assign({}, questions);
        newQuestions[key] = question;

        setQuestions(newQuestions);
    }

    /**
     * Deletes question in list given the index
     *
     * @param key key of question
     */
    const deleteQuestion = (key) => {
        let newQuestions = Object.assign({}, questions);
        delete newQuestions[key];

        setQuestions(newQuestions);
    }

    /**
     * Represents menu items to generate question editors.
     *
     * @return list of menu items
     */
    const MenuItems = () => {

        let questionList = surveyDefinition;

        let menuItems = [];

        for (let key in questionList.questions) {
            let questionId = questionList.questions[key].id;

            /**
             * Function to handle click.
             *
             * Generates unique id and pushes new question to list depending on the menu item.
             */
            const handleClick = () => {
                const id = generateUniqueID();

                let newQuestions = Object.assign({}, questions);
                newQuestions[id] = { questionId: questionId };
                setQuestions(newQuestions);

                handleQuestionMenuClose();
            }
            if(questionList.questions[key].dayOffset.includes(parseInt(selectedDayOffset))){
                menuItems.push(
                    <MenuItem key={key}
                              onClick={handleClick}>
                        {questionId + "?"}
                    </MenuItem>
                );
            }
        }

        return menuItems;
    };

    /**
     * Maps questions to unique keys (We can't simply used
     * the index of the array as it will change on delete)
     *
     * @return mapped question list
     */
    const mapQuestions = () => {
        const responseQuestions = originalResponse.questions;

        let mappedQuestions = {};

        // Map questions to key
        for (let index in responseQuestions) {
            const id = generateUniqueID();

            mappedQuestions[id] = responseQuestions[index];
        }

        return mappedQuestions;
    }

    const surveyDefinition = props.surveyDefinition;

    /**
     * Scans the surveyDefinition for dayOffsets.
     *
     * @returns list of dayOffsets from the survey definition
     */
    const getDayOffsets = () => {
        let offsets = []
        for (let question in props.surveyDefinition.questions) {
            offsets = offsets.concat(props.surveyDefinition.questions[question].dayOffset)
        }
        offsets = offsets.sort(function(a,b){return a-b});
        offsets = offsets.filter((item,index) => {
            return (offsets.indexOf(item) === index);
        });
        return offsets;
    }

    const [dayOffsetSet, setDayOffsetSet] = useState(false);
    const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
    const [jsonPreview, setJsonPreview] = useState({});
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [questionAnchorEl, setQuestionAnchorEl] = useState(null);
    const [sourceId, setSourceId]   = useState(originalResponse.sourceId);
    const [questions, setQuestions] = useState(mapQuestions());
    const [questionEditors, setQuestionEditors] = useState([]);
    const [selectedDayOffset, setSelectedDayOffset] = useState('');
    const [availableQuestions, setAvailableQuestions] = useState([]);
    const [origin, setOrigin] = useState('device');
    const [deviceSerial, setDeviceSerial] = useState('');
    const [patientId, setPatientId] = useState('');
    const [requirePatientConsent, setRequirePatientConsent] = useState(false);

    return (
        <>
            <Modal
                aria-labelledby="transition-modal-title"
                className='views-common-modal'
                open={props.open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={props.open}>
                    <div className='views-common-paper'>
                        <Dialog
                            open={warningDialogOpen}
                            onClose={handleWarningDialogClose}
                        >
                            <DialogTitle >{`${warningLabel} Response in ${props.publishTo}?`}</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to {warningLabel.toLowerCase()} this survey response to {props.publishTo}?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleWarningDialogClose}
                                    color="primary"
                                >
                                    Return
                                </Button>
                                <Button
                                    onClick={publishSQS}
                                    color="primary"
                                    autoFocus
                                >
                                    Confirm
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <div className='views-common-header'>
                            <>
                                <Typography variant="h5" style={{ margin: "10px" }} gutterBottom>
                                    {titleLabel} a Response
                                </Typography>
                            </>
                            <FormGroup row >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className='common-button'
                                    onClick={handleJsonPreviewOpen}
                                    startIcon={<VisibilityIcon />}
                                >
                                    JSON
                                </Button>
                                <SaveButton
                                    clickHandler={handleSave}
                                    saveType="Publish"
                                />
                                <CloseButton handleClose={handleClose} />
                            </FormGroup>
                        </div>
                        <div className='createFormView-body'>
                            <br/>
                            <div>
                                <FormControl variant="outlined" className='questionEditor-deviceDetailsInput'>
                                    <InputLabel id="dayOffset">Day Offset</InputLabel>
                                    <Select
                                        id="dayOffset-selector"
                                        value={selectedDayOffset}
                                        onChange={handleDayOffsetChange}
                                        label="Day Offset"
                                    >
                                        {
                                            getDayOffsets().map(o => (<MenuItem key={o} value={o}>{o}</MenuItem>))
                                        }
                                    </Select>
                                </FormControl>
                            </div>
                            {dayOffsetSet && <div>
                                <SourceIdEditor
                                    updateSourceId={updateSourceId}
                                    updateRequirePatientConsent={updateRequirePatientConsent}
                                    sourceId={sourceId}
                                />

                                <Modal
                                    open={jsonPreviewOpen}
                                    onClose={() => {setJsonPreviewOpen(false)}}
                                    className='views-common-modal'
                                >
                                    <Paper elevation={3} className='createFormView-jsonPaper'>
                                        <div className='createFormView-jsonTextWrapper'>
                                            <textarea
                                                className='createFormView-jsonText'
                                                value={JSON.stringify(jsonPreview, null, 2)}
                                                spellCheck={"false"}
                                            />
                                        </div>
                                    </Paper>
                                </Modal>
                            </div>}
                            <br />
                            {dayOffsetSet && <div className='views-common-header'>
                                <FormControl variant="outlined" className='questionEditor-deviceDetailsInput'>
                                    <InputLabel id="origin">Origin</InputLabel>
                                    <Select
                                        id="origin-selector"
                                        value={origin}
                                        onChange={handleOriginChange}
                                        label="Origin"
                                    >
                                        <MenuItem value={"device"}>Device</MenuItem>
                                        <MenuItem value={"myair"}>myAir</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    id="deviceSerial"
                                    label="Device Serial #"
                                    value={deviceSerial}
                                    variant="outlined"
                                    className='questionEditor-deviceDetailsInput'
                                    onChange={handleDeviceSerialChange}
                                />
                                <TextField
                                    id="patientId"
                                    label="Patient ID"
                                    value={patientId}
                                    variant="outlined"
                                    className='questionEditor-deviceDetailsInput'
                                    onChange={handlePatientIdChange}
                                />
                            </div>}
                            {dayOffsetSet && <QuestionIndexSelection
                                id="question-index-selection-menu"
                                anchorEl={questionAnchorEl}
                                keepMounted
                                open={Boolean(questionAnchorEl)}
                                onClose={handleQuestionMenuClose}
                            >
                                <MenuItems
                                    questionList={availableQuestions}
                                />
                            </QuestionIndexSelection>}
                            {dayOffsetSet && <Button
                                variant="outlined"
                                color="primary"
                                className='createFormView-button'
                                onClick={handleQuestionMenuOpen}
                                startIcon={<AddIcon />}
                            >
                                Add Question
                            </Button>}
                            {questionEditors}
                        </div>
                    </div>
                </Fade>
            </Modal>
        </>
    );
}